/* eslint-disable max-lines, @typescript-eslint/no-non-null-assertion, @typescript-eslint/no-explicit-any */
import { IGetNewbuildingPolygonsResponse } from '../../repositories/newbuilding-mvk/v2/get-newbuilding-polygons';

const debounce = require('lodash.debounce');
import * as React from 'react';

import { IAgentSchema, IOfferSchema } from '@cian/api-models/offer-card/v1/get-offer-data';
import { IExperiment } from '../../api/experiments';
import { ElementInViewport } from '../../common/components/elementInViewport';
import { Link } from '../../common/components/link';
import { IPointData, ISimilarOffersData } from '../../store/map_similar';
import { MapTab, TMapTab } from '../../store/map_tabs';
import { getOfferType, isCommercial } from '../../utils/offer_type';
import { loadYmapsApi } from '../../utils/ymaps';
import { PrintMapContainer } from '../print_map/container';
import { OfferMapGeoContainer } from './geo/container';
import { getSimilarPin, IconCurrentOffer } from './icons';
import { LegendContainer } from './legend/container';
import { RulerControls } from './ruler';
import { OfferMapTabsContainer } from './tabs/container';
import { trackMapInViewportClick, trackInteractivePlanClick } from './tracking';
import { ZoomControls } from './zoom';
import * as Analytics from '../../ebc/track_similar';

import { InfrastructureMapExtension } from './infrastructure-map-extention';

import * as styles from './index.css';
import { IEbcState } from '../../store/ebc';
import { INewbuildingPolygon, ISectionPolygon } from '@cian/mvk-widget';

export interface IRuler {
  isEnabled(): boolean;
  enable(): void;
  close(): void;
  disable(): void;
}

export interface IOfferMapProps {
  activeTab: TMapTab;
  coordinates: { lng: number; lat: number };
  experiments: IExperiment[];
  offer: IOfferSchema;
  offerPosition: [number, number];
  similarObjects?: ISimilarOffersData;
  viewedIds: Set<string>;
  agent: IAgentSchema | null;
  MVKData?: IGetNewbuildingPolygonsResponse;
  ebcInfo: IEbcState;
  newbuildingMvkUrl: string;
  user: Analytics.IUserEBC;
  page: Analytics.IPageEBC;

  changeTab(tab: TMapTab): void;
  fetchSimilar(bounds: [[number, number], [number, number]]): void;
  markViewed(id: string): void;
  openMvkWidget(sectionId?: number): void;
}

export interface IOfferMapState {
  isNoPanoramaStubVisible: boolean;
  isMapLoaded: boolean;
  isRuleActive: boolean;
}

export interface IDiagramSegmentDict {
  [key: string]: number; // color: weight
}

const INITIAL_ZOOM = 15;
const OFFER_PIN_ID = 'currentOfferIcon';

export class OfferMap extends React.Component<IOfferMapProps, IOfferMapState> {
  private mapContainer: HTMLDivElement | null;
  private map: YMaps.Map;
  private ymaps: YMaps.IYMaps;
  private mapObjectManager: YMaps.ObjectManager;

  private mapSimilarObjects: Object[][] | Object[] | String;
  private initialCenterAndZoom: YMaps.MapCenterAndZoom;

  private newbuildingPolygon: YMaps.GeoObject | null = null;
  private sectionPolygons: YMaps.GeoObject[] = [];

  private handleSimilarObjectClick: (e: { get(optionName: string): string }) => void;

  public state: IOfferMapState = {
    isMapLoaded: false,
    isNoPanoramaStubVisible: false,
    isRuleActive: false,
  };

  public componentDidMount() {
    const { coordinates } = this.props;

    this.props.changeTab(MapTab.similarOffers);

    this.initialCenterAndZoom = {
      center: [coordinates.lng, coordinates.lat],
      zoom: INITIAL_ZOOM,
    };

    loadYmapsApi({
      require: [
        'Map',
        'GeoObject',
        'ObjectManager',
        'templateLayoutFactory',
        'geometry.pixel.Rectangle',
        'util.bounds',
        'shape.Rectangle',
        'control.RulerControl',
        'objectManager.addon.objectsBalloon',
        'geoObject.addon.hint',
        'Placemark',
      ],
    })
      .then(ymaps => {
        this.ymaps = ymaps;
        // tslint:disable-next-line:no-non-null-assertion
        this.map = new ymaps.Map(this.mapContainer!, this.initialCenterAndZoom);
        this.map.behaviors.disable('scrollZoom');

        const rulerControl = new ymaps.control.RulerControl({
          options: {
            scaleLine: false,
            visible: false,
          },
        });

        this.map.controls.add(rulerControl);

        this.mapObjectManager = new ymaps.ObjectManager();
        this.map.geoObjects.add(this.mapObjectManager);

        this.mapObjectManager.add([createIcon([coordinates.lng, coordinates.lat], OFFER_PIN_ID, IconCurrentOffer, 10)]);
        this.mapObjectManager.events.add('click', this.handleofferPinClick);

        if (this.props.MVKData) {
          this.createNewbuildingPolygons(this.props.MVKData).catch(() => {});
        }

        this.setState({ isMapLoaded: true });

        switch (this.props.activeTab) {
          case MapTab.similarOffers:
            this.map.events.add('boundschange', this.debouncedUpdateSimilarHandler);
            const bounds = this.map.getBounds();
            this.props.fetchSimilar(bounds);
            if (this.props.similarObjects) {
              this.hideSimilar();
              this.showSimilar(this.props.similarObjects.data.points);
            }
            break;
          case MapTab.panorama:
            this.setPanoramaTab(this.props);
            break;
          case MapTab.default:
            break;
        }
      })
      .catch(e => {
        throw e;
      });
  }

  public UNSAFE_componentWillReceiveProps(nextProps: IOfferMapProps) {
    const { isMapLoaded } = this.state;

    if (nextProps.activeTab === MapTab.default && isMapLoaded) {
    } else if (this.props.activeTab === MapTab.default && isMapLoaded) {
      this.setCenterAndZoom(this.initialCenterAndZoom);
    }

    if (nextProps.activeTab !== this.props.activeTab) {
      if (nextProps.activeTab === MapTab.panorama && isMapLoaded) {
        this.setPanoramaTab(nextProps);
      } else if (this.props.activeTab === MapTab.panorama && isMapLoaded) {
        this.setState({
          isNoPanoramaStubVisible: false,
        });

        this.map
          .getPanoramaManager()
          .then(panoramaManager => {
            panoramaManager.closePlayer();
          })
          .catch(e => {
            throw e;
          });

        this.setCenterAndZoom(this.initialCenterAndZoom);
      }
    }

    if (nextProps.activeTab !== MapTab.similarOffers && isMapLoaded) {
      this.map.events.remove('boundschange', this.debouncedUpdateSimilarHandler);
    }

    if (nextProps.activeTab === MapTab.similarOffers && isMapLoaded) {
      if (this.props.activeTab !== MapTab.similarOffers) {
        this.map.events.add('boundschange', this.debouncedUpdateSimilarHandler);
        const bounds = this.map.getBounds();
        this.props.fetchSimilar(bounds);
      }
      if (nextProps.similarObjects) {
        this.hideSimilar();
        this.showSimilar(nextProps.similarObjects.data.points);
      }
    } else if (this.props.activeTab === MapTab.similarOffers && isMapLoaded) {
      this.setCenterAndZoom(this.initialCenterAndZoom);

      this.hideSimilar();
    }

    if (!this.props.MVKData && nextProps.MVKData) {
      this.createNewbuildingPolygons(nextProps.MVKData).catch(() => {});
    }

    if (this.props.activeTab !== nextProps.activeTab) {
      if (nextProps.activeTab === MapTab.default) {
        this.drawNewbuildingPolygons();
      } else {
        this.removeNewbuildingPolygons();
      }
    }
  }

  public render() {
    const { activeTab, offer, MVKData } = this.props;
    const { isMapLoaded, isNoPanoramaStubVisible } = this.state;
    const offerType = getOfferType(offer);

    return (
      <ElementInViewport
        onChange={isVisible => {
          if (isVisible) {
            trackMapInViewportClick(!!MVKData);
          }
        }}
      >
        <section className={styles['container']} id="map">
          {isCommercial(offerType) && <OfferMapGeoContainer />}
          <OfferMapTabsContainer />
          <PrintMapContainer />
          <div className={styles['map_container']}>
            {isNoPanoramaStubVisible && (
              <div className={styles['stub']}>
                <div className={styles['stub-content']}>
                  <strong className={styles['stub-title']}>Панорамы по этому адресу нет</strong>
                  <Link href="#" onClick={this.handleClickOnStubLink}>
                    Перейти на карту
                  </Link>
                </div>
              </div>
            )}
            <div ref={ref => (this.mapContainer = ref)} className={styles['map']} />
            {activeTab !== MapTab.panorama && isMapLoaded && (
              <ZoomControls onZoomIn={this.onZoomIn} onZoomOut={this.onZoomOut} />
            )}
            {activeTab !== MapTab.panorama && isMapLoaded && (
              <RulerControls onRulerToggle={this.toggleRuler} isSelected={this.state.isRuleActive} />
            )}
            {activeTab === MapTab.default && isMapLoaded && (
              <InfrastructureMapExtension
                map={this.map}
                objectManager={this.mapObjectManager}
                offer={this.props.offer}
              />
            )}
            {activeTab === MapTab.similarOffers && isMapLoaded && <LegendContainer />}
          </div>
        </section>
      </ElementInViewport>
    );
  }

  // если кликнули на пин объявления и есть МВК для этой объявки, открывает виджет МВК
  private handleofferPinClick = (event: { get(optionName: string): string }) => {
    const id: string = event.get('objectId');

    if (id === OFFER_PIN_ID && this.props.activeTab === MapTab.default && this.props.MVKData) {
      trackInteractivePlanClick();
      this.props.openMvkWidget();
    }
  };

  private handleClickOnStubLink = (e: React.MouseEvent<HTMLAnchorElement>) => {
    e.preventDefault();
    this.props.changeTab(MapTab.default);
  };

  private setPanoramaTab = (props: IOfferMapProps) => {
    const { coordinates } = props;

    this.map
      .getPanoramaManager()
      .then(panoramaManager => {
        panoramaManager
          .openPlayer([coordinates.lng, coordinates.lat])
          .then(() => {
            const player = panoramaManager.getPlayer();

            if (player) {
              player.events.add('destroy', () => {
                if (this.props.activeTab === MapTab.panorama) {
                  this.props.changeTab(MapTab.default);
                }
              });
            }
          })
          .catch(error => {
            if (this.props.activeTab === MapTab.panorama) {
              this.setState({
                isNoPanoramaStubVisible: true,
              });
            }
          });
      })
      .catch(e => {
        throw e;
      });
  };

  private showSimilar(points: { [index: string]: IPointData }) {
    const similarPointsIcons = [];

    for (let point in points) {
      if (points.hasOwnProperty(point)) {
        if (similarPointsIcons.length < 100) {
          similarPointsIcons.push(
            createIcon(
              point
                .split(' ')
                .map(Number)
                .reverse(),
              point,
              getSimilarPin(points[point], this.props.viewedIds.has ? this.props.viewedIds.has(point) : false),
              1,
            ),
          );
        }
      }
    }

    this.mapObjectManager.add(similarPointsIcons);
    this.mapSimilarObjects = similarPointsIcons;

    this.handleSimilarObjectClick = (e: { get(optionName: string): string }) => {
      const id: string = e.get('objectId');
      const { markViewed, offer, agent, user, page } = this.props;

      if (id in points) {
        const item = points[id];

        this.mapObjectManager.objects.setObjectOptions(id, {
          iconLayout: window.ymaps.templateLayoutFactory.createClass(getSimilarPin(points[id], true)),
        });

        if (item.content.offers_count > 1) {
          window.open(item.content.link);
        } else {
          window.open(extractHref(item.offers[0].link_text));
        }

        markViewed(id);
      }
      const curOffder = points[id].offers[0];

      const products: Analytics.TProductPartial[] = [
        {
          id: Number(curOffder.id),
          ownerId: curOffder.published_user_id,
          variant: curOffder.services,
        },
      ];

      Analytics.trackOfferClick({
        action: Analytics.EtrackOfferClicksActions.click_similar_on_map,
        products,
        agent,
        offer,
        modelVersion: '0',
        user,
        page,
      });
    };

    this.mapObjectManager.events.add('click', this.handleSimilarObjectClick);
  }

  private hideSimilar() {
    if (this.mapSimilarObjects) {
      this.mapObjectManager.remove(this.mapSimilarObjects as Object[]);
    }

    if (this.handleSimilarObjectClick) {
      this.mapObjectManager.events.remove('click', this.handleSimilarObjectClick);
    }
  }

  private onZoomIn = () => {
    this.map.setZoom(this.map.getZoom() + 1);
  };

  private onZoomOut = () => {
    this.map.setZoom(this.map.getZoom() - 1);
  };

  private updateSimilarHandler = () => {
    if (this.props.activeTab === MapTab.similarOffers) {
      const bounds = this.map.getBounds();
      this.props.fetchSimilar(bounds);
    }
  };

  private debouncedUpdateSimilarHandler = debounce(this.updateSimilarHandler, 500);

  private toggleRuler = () => {
    const ruler: IRuler = this.map.behaviors.get<IRuler>('ruler');
    const enabled = ruler.isEnabled();

    if (this.map && !enabled) {
      ruler.enable();
      this.setState({ isRuleActive: true });
    } else {
      ruler.close();
      ruler.disable();
      this.setState({ isRuleActive: false });
    }
  };

  private setCenterAndZoom = (сenterAndZoom: YMaps.MapCenterAndZoom) => {
    if (this.map) {
      this.map.setCenter(сenterAndZoom.center, сenterAndZoom.zoom);
    }
  };

  private createNewbuildingPolygons = async (MVKData: IGetNewbuildingPolygonsResponse) => {
    if (!this.ymaps || this.newbuildingPolygon) {
      return;
    }

    const {
      getColoredNewbuildingPolygon,
      getColoredSectionPolygons,
      setHoverOpacityToPolygon,
      getYmapsPolygonGeometryOptions,
    } = await import('@cian/mvk-widget');

    // создает полигон для карты
    const createYmapsPolygon = (polygon: ISectionPolygon | INewbuildingPolygon) => {
      return new this.ymaps.GeoObject(getYmapsPolygonGeometryOptions(polygon), polygon.props);
    };

    // создает полигон ЖК для карты
    /** Надо убирать api-models из mvk-widget CD-49347**/
    //tslint:disable-next-line:no-any
    const coloredNewbuildingPolygon = getColoredNewbuildingPolygon(MVKData.polygon as any);
    this.newbuildingPolygon = createYmapsPolygon(coloredNewbuildingPolygon);

    // меняем прозрачность полигона при наведении мыши
    const handleSectionMouseEnter = (event: YMaps.IEvent) => setHoverOpacityToPolygon(event.get('target'), true);
    const handleSectionMouseLeave = (event: YMaps.IEvent) => setHoverOpacityToPolygon(event.get('target'), false);

    // создает полигоны секций для карты
    // навешивает события клика и ховера
    /** Надо убирать api-models из mvk-widget CD-49347**/
    //tslint:disable-next-line:no-any
    const coloredSectionPolygons = getColoredSectionPolygons(MVKData as any, this.ymaps);
    this.sectionPolygons = coloredSectionPolygons.map(polygon => {
      const ymapsPolygon = createYmapsPolygon(polygon);

      ymapsPolygon.events.add('click', this.handleSectionClick);
      ymapsPolygon.events.add('mouseenter', handleSectionMouseEnter);
      ymapsPolygon.events.add('mouseleave', handleSectionMouseLeave);

      return ymapsPolygon;
    });

    if (this.props.activeTab === MapTab.default) {
      this.drawNewbuildingPolygons();
    }
  };

  private handleSectionClick = (event: YMaps.IEvent) => {
    const target = event.get('target');
    const sectionId = Number(target.options.get('id')) || undefined;
    this.props.openMvkWidget(sectionId);
    trackInteractivePlanClick();
  };

  private drawNewbuildingPolygons = () => {
    if (!this.newbuildingPolygon || !this.mapContainer || !this.ymaps) {
      return;
    }

    this.drawPolygons([this.newbuildingPolygon, ...this.sectionPolygons]);

    const newbuildingPolygonBounds = this.newbuildingPolygon.geometry && this.newbuildingPolygon.geometry.getBounds();

    // вычисляем центр и зум чтобы вписать полигон ЖК в видимую часть
    if (this.mapContainer && newbuildingPolygonBounds) {
      const { width, height } = this.mapContainer.getBoundingClientRect();
      this.initialCenterAndZoom = this.ymaps.util.bounds.getCenterAndZoom(newbuildingPolygonBounds, [width, height]);
      this.setCenterAndZoom(this.initialCenterAndZoom);
    }
  };

  private removeNewbuildingPolygons = () => {
    this.removePolygons([this.newbuildingPolygon, ...this.sectionPolygons]);
  };

  private drawPolygons = (polygons: (YMaps.GeoObject | null)[]) => {
    polygons.forEach(polygon => {
      if (polygon) {
        this.map.geoObjects.add(polygon);
      }
    });
  };

  private removePolygons = (polygons: (YMaps.GeoObject | null)[]) => {
    polygons.forEach(polygon => {
      if (polygon) {
        this.map.geoObjects.remove(polygon);
      }
    });
  };
}

function createIcon(offerPosition: [number, number] | number[], id: number | string, icon: string, zIndex?: number) {
  return {
    geometry: {
      coordinates: offerPosition,
      type: 'Point',
    },
    id,
    options: {
      iconLayout: window.ymaps.templateLayoutFactory.createClass(icon),
      iconOffset: [-27, -61],
      iconShape: {
        coordinates: [27, 27],
        radius: 26.5,
        type: 'Circle',
      },
      iconSize: [54, 61],
      zIndex,
    },
    type: 'Feature',
  };
}

function extractHref(href: string[]) {
  const link = href[href.length - 1].match(/href="([^"]*)/);
  if (link) {
    return link[1];
  }

  return '';
}
